import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBookkeepingAccountApi,
  deleteBookkeepingAccountApi,
  getBookkeepingAccountApi,
  getBookkeepingAccountsApi,
  updateBookkeepingAccountApi,
} from "../api/bookkeepingAccountsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { BookkeepingAccountId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateBookkeepingAccount,
  RestUpdateBookkeepingAccount,
} from "@KassaroAB/shared/types/bookkeepingAccount";

const bookkeepingAccountsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "bookkeepingAccounts");

const invalidateBookkeepingAccountQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({
    queryKey: bookkeepingAccountsQueryKey(scope),
  });
};

export const useGetBookkeepingAccountsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: bookkeepingAccountsQueryKey(scope),
    queryFn: () => getBookkeepingAccountsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetBookkeepingAccountQuery = (
  scope: CatalogScope | null,
  accountId: BookkeepingAccountId | null | undefined,
) => {
  return useQuery({
    queryKey: [...bookkeepingAccountsQueryKey(scope), accountId],
    queryFn: () => getBookkeepingAccountApi(scope!, accountId!),
    enabled: !!scope && !!accountId,
  });
};

export const useCreateBookkeepingAccountMutation = (
  scope: CatalogScope | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (account: RestCreateBookkeepingAccount) =>
      createBookkeepingAccountApi(scope!, account),
    onSuccess: () => {
      invalidateBookkeepingAccountQueries(queryClient, scope);
    },
  });
};

export const useUpdateBookkeepingAccountMutation = (
  scope: CatalogScope | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      account,
    }: {
      accountId: BookkeepingAccountId;
      account: RestUpdateBookkeepingAccount;
    }) => updateBookkeepingAccountApi(scope!, accountId, account),
    onSuccess: () => {
      invalidateBookkeepingAccountQueries(queryClient, scope);
    },
  });
};

export const useDeleteBookkeepingAccountMutation = (
  scope: CatalogScope | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: BookkeepingAccountId) =>
      deleteBookkeepingAccountApi(scope!, accountId),
    onSuccess: () => {
      invalidateBookkeepingAccountQueries(queryClient, scope);
    },
  });
};
